import { useEffect, useMemo, useState } from 'react'
import type { PmmRepository } from '../data/repository'
import { ONBOARDING_FIELD_MAP } from '../domain/onboarding'
import {
  displayFactValue,
  parseEditedFactValue,
  provenanceMeta,
  type ProfileFact,
  type RelationshipIntelligenceBundle,
  type SpineHypothesis,
} from '../domain/intelligence'

interface RelationshipIntelligenceHubProps {
  repository: PmmRepository
}

const factLabel = (key: string) => ONBOARDING_FIELD_MAP.get(key)?.label ?? key.replaceAll('.', ' · ')

const sectionMeta = {
  strengthFactIds: ['Strengths already present', 'What is working or already available to build from.'],
  priorityFactIds: ['Current priorities', 'What you have said deserves deliberate attention now.'],
  constraintFactIds: ['Real-world constraints', 'Capacity, load and context that should shape the dose.'],
  partnerReportFactIds: ['Partner information you reported', 'These remain your report, never partner-direct truth.'],
} as const

function FactCard({ fact, onSelect }: { fact: ProfileFact; onSelect: (fact: ProfileFact) => void }) {
  const meta = provenanceMeta[fact.provenance]
  return (
    <article className={`profile-fact fact-${fact.state}`}>
      <header><span>{meta.label}</span><small>Revision {fact.revision}</small></header>
      <h4>{factLabel(fact.key)}</h4>
      <p>{displayFactValue(fact.value)}</p>
      <footer>
        <span>{fact.state}</span>
        <button type="button" onClick={() => onSelect(fact)}>Correct or withdraw</button>
      </footer>
    </article>
  )
}

function HypothesisCard({ hypothesis, onReview }: { hypothesis: SpineHypothesis; onReview: (hypothesis: SpineHypothesis) => void }) {
  return (
    <article className={`spine-hypothesis hypothesis-${hypothesis.state}`}>
      <header><span>App hypothesis · {hypothesis.confidence} confidence</span><small>{hypothesis.state.replaceAll('_', ' ')}</small></header>
      <p className="hypothesis-statement">{hypothesis.userEditedStatement ?? hypothesis.statement}</p>
      <div className="alternative-lens"><strong>Another explanation</strong><p>{hypothesis.alternativeExplanation}</p></div>
      <footer><span>Expires {new Date(hypothesis.expiresAt).toLocaleDateString()}</span>{!['rejected', 'invalidated', 'expired'].includes(hypothesis.state) && <button type="button" onClick={() => onReview(hypothesis)}>Review hypothesis</button>}</footer>
    </article>
  )
}

export function RelationshipIntelligenceHub({ repository }: RelationshipIntelligenceHubProps) {
  const [bundle, setBundle] = useState<RelationshipIntelligenceBundle | null>(null)
  const [view, setView] = useState<'spine' | 'facts' | 'history'>('spine')
  const [selectedFact, setSelectedFact] = useState<ProfileFact | null>(null)
  const [selectedHypothesis, setSelectedHypothesis] = useState<SpineHypothesis | null>(null)
  const [editedValue, setEditedValue] = useState('')
  const [reason, setReason] = useState('')
  const [hypothesisEdit, setHypothesisEdit] = useState('')
  const [notice, setNotice] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    setLoading(true)
    void repository.getRelationshipIntelligence().then((result) => {
      if (active) setBundle(result)
    }).catch(() => {
      if (active) setNotice('Your intelligence spine could not be loaded. No browser copy was created.')
    }).finally(() => {
      if (active) setLoading(false)
    })
    return () => { active = false }
  }, [repository])

  const factMap = useMemo(() => new Map(bundle?.facts.map((fact) => [fact.factId, fact]) ?? []), [bundle])

  function beginFactReview(fact: ProfileFact) {
    setSelectedFact(fact)
    setEditedValue(Array.isArray(fact.value) ? fact.value.join(', ') : String(fact.value))
    setReason('')
  }

  async function correctFact(action: 'correct' | 'dispute' | 'revoke') {
    if (!selectedFact) return
    try {
      const replacementValue = action === 'correct' ? parseEditedFactValue(editedValue, selectedFact.value) : undefined
      const result = await repository.correctProfileFact({
        factId: selectedFact.factId,
        expectedRevision: selectedFact.revision,
        action,
        replacementValue,
        reason: reason.trim() || (action === 'revoke' ? 'Withdrawn by user' : 'Corrected by user'),
        clientMutationId: crypto.randomUUID(),
      })
      setBundle(result)
      setSelectedFact(null)
      setNotice(action === 'revoke' ? 'The fact was withdrawn and dependent hypotheses were invalidated.' : 'A new immutable fact revision and spine version were created.')
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'The correction could not be saved.')
    }
  }

  async function reviewHypothesis(decision: 'confirm_for_now' | 'reject' | 'edit') {
    if (!bundle || !selectedHypothesis) return
    try {
      const result = await repository.reviewSpineHypothesis({
        spineVersionId: bundle.spine.spineVersionId,
        hypothesisId: selectedHypothesis.hypothesisId,
        decision,
        editedStatement: decision === 'edit' ? hypothesisEdit.trim() : undefined,
        clientMutationId: crypto.randomUUID(),
      })
      setBundle(result)
      setSelectedHypothesis(null)
      setNotice(decision === 'confirm_for_now' ? 'Confirmed as a temporary hypothesis—not promoted to fact.' : 'A new immutable spine version was created.')
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'The hypothesis review could not be saved.')
    }
  }

  if (loading) return <section className="intelligence-loading" aria-live="polite"><span /><p>Building a source-labelled view of what you actually said…</p></section>
  if (!bundle) return <section className="intelligence-error"><h2>Your intelligence spine is unavailable</h2><p>{notice}</p></section>

  return (
    <section className="intelligence-hub" aria-labelledby="intelligence-title">
      <header className="intelligence-heading">
        <div><p className="eyebrow">Your living relationship intelligence</p><h2 id="intelligence-title">Facts stay facts. <em>Hypotheses stay tentative.</em></h2><p>This is a visible working map, not a secret psychological profile. You can correct or withdraw every current statement.</p></div>
        <div className="spine-version"><span>Current version</span><strong>v{bundle.spine.versionNumber}</strong><small>{bundle.spine.userReviewState.replace('_', ' ')}</small></div>
      </header>

      <nav className="intelligence-tabs" aria-label="Relationship intelligence views">
        <button type="button" className={view === 'spine' ? 'is-active' : ''} onClick={() => setView('spine')}>Relationship spine</button>
        <button type="button" className={view === 'facts' ? 'is-active' : ''} onClick={() => setView('facts')}>All current facts ({bundle.facts.filter((fact) => fact.state !== 'revoked').length})</button>
        <button type="button" className={view === 'history' ? 'is-active' : ''} onClick={() => setView('history')}>Immutable history ({bundle.factHistory.length})</button>
      </nav>

      {view === 'spine' && <div className="spine-view">
        {(Object.keys(sectionMeta) as Array<keyof typeof sectionMeta>).map((sectionKey) => {
          const ids = bundle.spine[sectionKey]
          if (ids.length === 0) return null
          return <section className="spine-section" key={sectionKey}><div><h3>{sectionMeta[sectionKey][0]}</h3><p>{sectionMeta[sectionKey][1]}</p></div><div className="fact-grid">{ids.map((id) => factMap.get(id)).filter((fact): fact is ProfileFact => Boolean(fact)).map((fact) => <FactCard key={fact.factId} fact={fact} onSelect={beginFactReview} />)}</div></section>
        })}
        <section className="spine-section hypotheses-section"><div><h3>Tentative patterns to test</h3><p>A hypothesis is never a diagnosis or identity label. Confirmation keeps it temporary and expiring.</p></div><div>{bundle.spine.hypotheses.length ? bundle.spine.hypotheses.map((hypothesis) => <HypothesisCard key={hypothesis.hypothesisId} hypothesis={hypothesis} onReview={(item) => { setSelectedHypothesis(item); setHypothesisEdit(item.userEditedStatement ?? item.statement) }} />) : <p className="empty-intelligence">No active hypotheses. The app will not invent one merely to fill this section.</p>}</div></section>
        <section className="spine-section missingness-section"><div><h3>What is still unknown</h3><p>Missing data remains visible instead of being guessed.</p></div><ul>{bundle.spine.missingness.map((item) => <li key={item.key}><strong>{item.prompt}</strong><span>{item.reason.replaceAll('_', ' ')}</span></li>)}</ul></section>
      </div>}

      {view === 'facts' && <div className="all-facts"><div className="provenance-guide">{Object.entries(provenanceMeta).filter(([key]) => key !== 'partner_direct').map(([key, meta]) => <div key={key}><strong>{meta.label}</strong><span>{meta.description}</span></div>)}</div><div className="fact-grid">{bundle.facts.filter((fact) => fact.state !== 'revoked').map((fact) => <FactCard key={fact.factId} fact={fact} onSelect={beginFactReview} />)}</div></div>}

      {view === 'history' && <div className="history-list">{bundle.factHistory.map((version) => <article key={version.versionId}><span>{version.versionId}</span><strong>{factLabel(version.key)}</strong><p>{displayFactValue(version.value)}</p><small>{version.state} · {provenanceMeta[version.provenance].label} · {new Date(version.effectiveAt).toLocaleString()}</small></article>)}</div>}

      {selectedFact && <div className="intelligence-dialog-backdrop" role="presentation"><section className="intelligence-dialog" role="dialog" aria-modal="true" aria-labelledby="fact-dialog-title"><p className="eyebrow">Correct the record</p><h3 id="fact-dialog-title">{factLabel(selectedFact.key)}</h3><p className="dialog-provenance">{provenanceMeta[selectedFact.provenance].label}: {provenanceMeta[selectedFact.provenance].description}</p><label><span>Current or corrected value</span>{typeof selectedFact.value === 'boolean' ? <select value={editedValue} onChange={(event) => setEditedValue(event.target.value)}><option value="true">Yes</option><option value="false">No</option></select> : <textarea rows={5} value={editedValue} onChange={(event) => setEditedValue(event.target.value)} />}</label><label><span>Why are you changing this?</span><input value={reason} maxLength={240} onChange={(event) => setReason(event.target.value)} /></label><div className="dialog-actions"><button type="button" onClick={() => setSelectedFact(null)}>Cancel</button><button type="button" onClick={() => void correctFact('dispute')}>Mark disputed</button><button type="button" className="withdraw-action" onClick={() => void correctFact('revoke')}>Withdraw</button><button type="button" className="primary-action" onClick={() => void correctFact('correct')}>Save corrected revision</button></div></section></div>}

      {selectedHypothesis && <div className="intelligence-dialog-backdrop" role="presentation"><section className="intelligence-dialog" role="dialog" aria-modal="true" aria-labelledby="hypothesis-dialog-title"><p className="eyebrow">Review a tentative pattern</p><h3 id="hypothesis-dialog-title">This can never become a fact automatically.</h3><textarea rows={5} value={hypothesisEdit} maxLength={800} onChange={(event) => setHypothesisEdit(event.target.value)} /><div className="alternative-lens"><strong>Keep another explanation visible</strong><p>{selectedHypothesis.alternativeExplanation}</p></div><div className="dialog-actions"><button type="button" onClick={() => setSelectedHypothesis(null)}>Cancel</button><button type="button" className="withdraw-action" onClick={() => void reviewHypothesis('reject')}>Reject</button><button type="button" onClick={() => void reviewHypothesis('edit')}>Save as edited hypothesis</button><button type="button" className="primary-action" onClick={() => void reviewHypothesis('confirm_for_now')}>Confirm for now</button></div></section></div>}

      {notice && <div className="intelligence-notice" role="status">{notice}<button type="button" onClick={() => setNotice('')} aria-label="Dismiss intelligence message">×</button></div>}
    </section>
  )
}
